'use client';

import dynamic from 'next/dynamic';
import { useTheme } from 'next-themes';

import type { GeoMapProps } from '@skrym/shared/components/geo-map';
import type { RoutingProps } from '@skrym/shared/components/routing';
import { Skeleton } from '@skrym/shared/ui/skeleton';

import { clientSideApi } from '@/lib/apiClient/client-side';
import { clientSideEnv } from '@/lib/env/client-side';

import type { locations } from '../../shared/lib/api-client/encore-client';

export const GeoMapComponent = dynamic(() => import('@skrym/shared/components/geo-map'), {
  ssr: false,
  loading: () => <Skeleton className="grow" />,
});

// A default implementation to enable passing env vars
export function GeoMap(props: Omit<GeoMapProps, 'tileLayerURL'>) {
  const token = clientSideEnv.NEXT_PUBLIC_MAPBOX_TOKEN;
  const username = clientSideEnv.NEXT_PUBLIC_MAPBOX_USERNAME;
  const lightModeStyle = clientSideEnv.NEXT_PUBLIC_MAPBOX_LIGHTMODE_STYLE;
  const darkModeStyle = clientSideEnv.NEXT_PUBLIC_MAPBOX_DARKMODE_STYLE;

  const { resolvedTheme } = useTheme();
  const url = `https://api.mapbox.com/styles/v1/${username}/${resolvedTheme === 'dark' ? darkModeStyle : lightModeStyle}/tiles/256/{z}/{x}/{y}@2x?access_token=${token}`;

  return <GeoMapComponent tileLayerURL={url} {...props} />;
}

export const RoutingComponent = dynamic(() => import('@skrym/shared/components/routing'), {
  ssr: false,
});

// A default implementation using our ORS
export function Routing(props: RoutingProps) {
  const defaultRouterFunction = (params: locations.ORSDirectionsGeoJSONParams) =>
    clientSideApi.locations.ORSDirectionsGeoJSON(params);
  return (
    <RoutingComponent {...props} routerFunction={props.routerFunction || defaultRouterFunction} />
  );
}

export const CustomMarker = dynamic(
  () => import('@skrym/shared/components/geo-map').then((mod) => mod.CustomMarker),
  {
    ssr: false,
  },
);
